import { useState } from "react";
import { CaretDown, CaretRight } from "@phosphor-icons/react";
import { TIERS, convert, money } from "./tiersShared";

function MarginCell({ row, cur, rate }) {
  const pct = Number(row.margin_pct || 0);
  const neg = pct < 0;
  const width = Math.min(100, Math.max(0, Math.abs(pct)));
  return (
    <div className="min-w-[140px]">
      <div className={`numeric font-semibold ${neg ? "text-[#E0000F]" : "text-[#00873D]"}`}>{money(convert(row.margin_usd, cur, rate), cur)}</div>
      <div className="flex items-center gap-2 mt-1">
        <div className="flex-1 h-1.5 bg-zinc-100 rounded-full overflow-hidden">
          <div className={`h-full ${neg ? "bg-[#E0000F]" : "bg-[#00873D]"}`} style={{ width: `${width}%` }} />
        </div>
        <span className={`text-[11px] numeric ${neg ? "text-[#E0000F]" : "text-zinc-600"}`}>{pct.toFixed(1)}%</span>
      </div>
    </div>
  );
}

function Row({ row, cur, rate, color, depth = 0, expandable, expanded, onToggle, testid }) {
  return (
    <tr
      className={`border-t border-zinc-100 ${depth === 0 ? "hover:bg-zinc-50 cursor-pointer" : "bg-zinc-50/60"}`}
      onClick={depth === 0 && expandable ? onToggle : undefined}
      data-testid={testid}
    >
      <td className="px-4 py-3">
        <div className="flex items-center gap-2" style={{ paddingLeft: depth * 18 }}>
          {depth === 0 ? (
            <>
              {expandable ? (expanded ? <CaretDown size={12} weight="bold" className="text-zinc-400" /> : <CaretRight size={12} weight="bold" className="text-zinc-400" />) : <span className="w-3" />}
              <span className="w-2.5 h-2.5 rounded-sm shrink-0" style={{ background: color }} />
              <span className="font-semibold text-zinc-900">{row.name}</span>
            </>
          ) : (
            <span className="text-zinc-600 text-xs">↳ {row.name}</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3 text-right numeric">{row.items}</td>
      <td className="px-4 py-3 text-right numeric text-zinc-500">{row.products}</td>
      {TIERS.map((t) => (
        <td key={t.key} className={`px-4 py-3 text-right numeric ${t.key === "store" ? "font-semibold text-[#002FA7]" : "text-zinc-700"}`}>
          {money(convert(row[`${t.key}_usd`], cur, rate), cur)}
        </td>
      ))}
      <td className="px-4 py-3"><MarginCell row={row} cur={cur} rate={rate} /></td>
    </tr>
  );
}

export default function TiersTable({ categories, totals, cur, rate, colorOf }) {
  const [open, setOpen] = useState({});
  const toggle = (name) => setOpen((s) => ({ ...s, [name]: !s[name] }));
  return (
    <div className="border border-zinc-200 bg-white overflow-x-auto" data-testid="tiers-table">
      <table className="w-full text-sm">
        <thead className="bg-zinc-50 text-[10px] uppercase tracking-wider text-zinc-500">
          <tr>
            <th className="text-left px-4 py-2.5 font-semibold">Categoría</th>
            <th className="text-right px-4 py-2.5 font-semibold">Piezas</th>
            <th className="text-right px-4 py-2.5 font-semibold">Productos</th>
            {TIERS.map((t) => (
              <th key={t.key} className="text-right px-4 py-2.5 font-semibold">
                <span className="inline-flex items-center gap-1.5"><span className="w-2 h-2 rounded-sm" style={{ background: t.color }} />{t.label}</span>
              </th>
            ))}
            <th className="text-left px-4 py-2.5 font-semibold">Margen potencial (Tienda − Costo)</th>
          </tr>
        </thead>
        <tbody>
          {categories.map((c, i) => (
            <FragmentRows key={c.name} c={c} i={i} cur={cur} rate={rate} colorOf={colorOf} open={!!open[c.name]} onToggle={() => toggle(c.name)} />
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-zinc-200 bg-zinc-50 font-semibold" data-testid="tiers-total-row">
            <td className="px-4 py-3">Total disponible</td>
            <td className="px-4 py-3 text-right numeric">{totals.items}</td>
            <td className="px-4 py-3 text-right numeric text-zinc-500">{totals.products}</td>
            {TIERS.map((t) => (
              <td key={t.key} className={`px-4 py-3 text-right numeric ${t.key === "store" ? "text-[#002FA7]" : ""}`}>{money(convert(totals[`${t.key}_usd`], cur, rate), cur)}</td>
            ))}
            <td className="px-4 py-3"><MarginCell row={totals} cur={cur} rate={rate} /></td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function FragmentRows({ c, i, cur, rate, colorOf, open, onToggle }) {
  const brands = c.brands || [];
  return (
    <>
      <Row row={c} cur={cur} rate={rate} color={colorOf(i)} expandable={brands.length > 0} expanded={open} onToggle={onToggle} testid={`tiers-row-${c.name}`} />
      {open && brands.map((b) => (
        <Row key={b.name} row={b} cur={cur} rate={rate} depth={1} testid={`tiers-brand-${c.name}-${b.name}`} />
      ))}
    </>
  );
}
